/**
 * Sentry client — initialisation et wrappers React.
 *
 * Initié côté client avec :
 *  - DSN depuis window.__SENTRY_DSN_CLIENT (injecté par le serveur)
 *  - Tags funnel/step pour contextualiser les erreurs
 *  - Boundary React pour les composants critiques (funnels)
 */

import type { ComponentType, ReactNode } from 'react';

let initialized = false;

function getSentryKey(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  return (window as unknown as { __SENTRY_DSN_CLIENT?: string }).__SENTRY_DSN_CLIENT;
}

/**
 * Initialise Sentry côté client.
 * À appeler une fois depuis le Layout root après hydration.
 */
export async function initSentryClient(): Promise<void> {
  if (initialized || typeof window === 'undefined') return;
  const dsn = getSentryKey();
  if (!dsn) return;

  try {
    const Sentry = await import('@sentry/react');

    Sentry.init({
      dsn,
      environment: (window as unknown as { __APP_ENV?: string }).__APP_ENV ?? 'production',
      release: (window as unknown as { __APP_RELEASE?: string }).__APP_RELEASE,
      tracesSampleRate: 0.05,
      beforeSend(event) {
        // Ne pas envoyer les erreurs ResizeObserver (bruit Chrome)
        const msg = event.exception?.values?.[0]?.value ?? '';
        if (msg.includes('ResizeObserver') || msg.includes('Non-Error promise rejection')) {
          return null;
        }
        return event;
      },
    });
    initialized = true;
  } catch {
    // Sentry n'est pas une dépendance critique — fail silently
  }
}

/**
 * Capture une exception dans Sentry avec contexte funnel.
 */
export function captureException(
  err: unknown,
  context?: { funnel?: string; step?: string; leadId?: string },
): void {
  if (!initialized || typeof window === 'undefined') return;
  void import('@sentry/react').then((Sentry) => {
    Sentry.withScope((scope) => {
      if (context?.funnel) scope.setTag('funnel', context.funnel);
      if (context?.step) scope.setTag('funnel_step', context.step);
      if (context?.leadId) scope.setUser({ id: context.leadId });
      Sentry.captureException(err);
    });
  });
}

/** Type-only re-export pour éviter d'importer @sentry/react directement depuis les composants. */
export type ErrorBoundaryProps = {
  fallback: ReactNode;
  children: ReactNode;
  onError?: (error: Error) => void;
};

export type WithErrorBoundary<P> = ComponentType<P>;
